import { Component, type ErrorInfo, type ReactNode } from 'react';
import { makeStyles } from 'tss-react/mui';
import { getColors, typography } from '@mercedes-benz/odp-theme-v2';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

const useStyles = makeStyles()(theme => {
  const c = getColors(theme.palette.mode);
  return {
    root: {
      background: c.background.paper,
      borderRadius: 12,
      border: `1px solid ${c.border.default}`,
      padding: 48,
      minHeight: 200,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 12,
      textAlign: 'center',
    },
    icon: {
      width: 40,
      height: 40,
      color: c.text.secondary,
      opacity: 0.5,
    },
    title: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '1rem',
      fontWeight: 600,
      color: c.text.primary,
      margin: 0,
    },
    message: {
      fontFamily: typography.fontFamilyCorpoSText,
      fontSize: '0.875rem',
      color: c.text.secondary,
      margin: 0,
      maxWidth: 480,
    },
  };
});

const ErrorFallback = ({ message }: { message: string }) => {
  const { classes } = useStyles();
  return (
    <div className={classes.root}>
      <WarningAmberIcon className={classes.icon} />
      <p className={classes.title}>This tab could not be loaded</p>
      <p className={classes.message}>{message}</p>
    </div>
  );
};

interface TabErrorBoundaryProps {
  children: ReactNode;
}

interface TabErrorBoundaryState {
  error: Error | null;
}

export class TabErrorBoundary extends Component<
  TabErrorBoundaryProps,
  TabErrorBoundaryState
> {
  static getDerivedStateFromError(error: Error): TabErrorBoundaryState {
    return { error };
  }

  constructor(props: TabErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  componentDidCatch(_error: Error, _info: ErrorInfo) {
    // intentionally silent — the fallback UI is the feedback
  }

  render() {
    if (this.state.error) {
      return <ErrorFallback message={this.state.error.message} />;
    }
    return this.props.children;
  }
}
